import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import useAbortRegistry from '@dr-pam/common-components/Hooks/useAbortRegistry';
import { useMemo } from 'react';
import FetchUtils from '@dr-pam/common-components/Utils/FetchUtils';
import type {
	Category,
	ResourceHubFilterOptions,
	ResourceHubResource,
	ResourceType,
} from '@dr-pam/common-components/Models/NdcResourceHub';
import type { RegisterAbortFunction } from '@dr-pam/common-components/Utils/AbortUtils';
import type { HubIdentifier } from '../pages/ResourceHubPage';

export default class ResourceHubService {
	constructor(
		private readonly _apolloClient: ApolloClient,
		private readonly _registerAbort?: RegisterAbortFunction,
	) {}

	public getAccessibleHubs() {
		const request = FetchUtils.getJson<HubIdentifier[]>(`/api/ndc/resource-hub`);
		return FetchUtils.abortableRequest(request, this._registerAbort);
	}

	public getCategories(hubIdentifier: string) {
		const request = FetchUtils.getJson<Category[]>(`/api/ndc/resource-hub/${hubIdentifier}/categories`);
		return FetchUtils.abortableRequest(request, this._registerAbort);
	}

	public getResourceTypes(hubIdentifier: string) {
		const request = FetchUtils.getJson<ResourceType[]>(`/api/ndc/resource-hub/${hubIdentifier}/resource-types`);
		return FetchUtils.abortableRequest(request, this._registerAbort);
	}

	public getRecentResources(hubIdentifier: string) {
		const request = FetchUtils.getJson<ResourceHubResource[]>(`/api/ndc/resource-hub/${hubIdentifier}/recent`);
		return FetchUtils.abortableRequest(request, this._registerAbort);
	}

	public filterResources(hubIdentifier: string, options: ResourceHubFilterOptions) {
		const request = FetchUtils.postJson<ResourceHubResource[]>(
			`/api/ndc/resource-hub/${hubIdentifier}/filter`,
			options,
		);
		return FetchUtils.abortableRequest(request, this._registerAbort);
	}
}

export function useResourceHubService() {
	const apolloClient = useApolloClient();
	const registerAbort = useAbortRegistry();

	return useMemo(() => new ResourceHubService(apolloClient, registerAbort), [apolloClient, registerAbort]);
}
